import { useContext, useState } from "react"
import { AppContext } from "../App"
import { callApi, getInlineLoader, normalizePhoneNumber } from "../Helpers"
import { MdArrowBack, MdLock, MdLogin, MdPhone, MdRefresh } from "react-icons/md"

export default function AccountLogin({ onLogin = null }) {

  const appContext = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');

  function isValidPhone() {
    return Boolean(normalizePhoneNumber(phone));
  }

  async function completeLogin(user) {
    if (!user?.id || !user?.token) {
      appContext.tellError('Could not complete login');
      return;
    }

    const authenticated = await appContext.completeLogin(user);
    if (authenticated) {
      appContext.tellMessage('Welcome to MIKA KENYATA');
      if (onLogin) {
        onLogin();
      }
    } else {
      appContext.tellError('Could not complete login');
    }
  }

  async function submitPhone() {
    if (!isValidPhone()) {
      appContext.tellError('Invalid phone number');
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phone);
    setPhone(normalizedPhone);
    setLoading(true);
    await callApi('phone-login', { phone: normalizedPhone }).then((response) => {
      if (response.status === 1) {
        if (response.nextLoginStep === 'password') {
          setStep('password');
        } else if (response.nextLoginStep === 'otp') {
          setStep('otp');
          appContext.tellMessage('An OTP has been sent to your phone');
        } else {
          appContext.tellError('Could not determine the next login step');
        }
      } else {
        appContext.tellError(response.msg);
      }
    })
    setLoading(false);
  }

  async function submitPassword() {
    if (!password || password.trim().length === 0) {
      appContext.tellError('Invalid password');
      return;
    }

    setLoading(true);
    await callApi('password-login', {
      phone: phone.trim(),
      password,
    }).then(async (response) => {
      if (response.status === 1) {
        await completeLogin(response.user);
      } else {
        appContext.tellError(response.msg);
      }
    })
    setLoading(false);
  }

  async function submitOtp() {
    if (!otp || otp.trim().length !== 6) {
      appContext.tellError('Invalid OTP');
      return;
    }

    if (!password || password.trim().length < 6) {
      appContext.tellError('Password must have at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      appContext.tellError('Passwords do not match');
      return;
    }

    setLoading(true);
    await callApi('otp-login', {
      phone: phone.trim(),
      otp: otp.trim(),
      password,
    }).then(async (response) => {
      if (response.status === 1) {
        await completeLogin(response.user);
      } else {
        appContext.tellError(response.msg);
      }
    })
    setLoading(false);
  }

  async function forgotPassword() {
    setLoading(true);
    await callApi('forgot-password', { phone: phone.trim() }).then((response) => {
      if (response.status === 1) {
        setPassword('');
        setConfirmPassword('');
        setOtp('');
        setStep('otp');
        appContext.tellMessage('An OTP has been sent to your phone');
      } else {
        appContext.tellError(response.msg);
      }
    })
    setLoading(false);
  }

  async function resendOtp() {
    setLoading(true);
    await callApi('resend-otp', { phone: phone.trim() }).then((response) => {
      if (response.status === 1) {
        appContext.tellMessage('A new OTP has been sent to your phone');
      } else {
        appContext.tellError(response.msg);
      }
    })
    setLoading(false);
  }

  function changePhone() {
    setStep('phone');
    setPassword('');
    setConfirmPassword('');
    setOtp('');
  }

  return (
    <div className="customer-auth-shell">
      <div className="card customer-auth-card">
        <div className="card-body">
          <span className="marketplace-eyebrow text-primary">CUSTOMER ACCOUNT</span>

          {
            step === 'phone' ?
              <>
                <h6 className="customer-auth-title">Sign in or create an account</h6>
                <p className="customer-auth-description">
                  Enter your phone number. New customers will be guided through OTP registration automatically.
                </p>

                <div className="input-group mb-4">
                  <span className="input-group-text"><MdPhone className="text-primary" size={21}/></span>
                  <input
                    className="form-control"
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && submitPhone()}
                    placeholder="Phone number"
                  />
                </div>

                <button
                  className="btn btn-primary btn-rounded w-100 customer-auth-submit"
                  onClick={submitPhone}
                  disabled={loading}
                >
                  {loading ? getInlineLoader() : <>Continue <MdLogin size={22}/></>}
                </button>
              </>
              : ""
          }

          {
            step === 'password' ?
              <>
                <button className="customer-auth-back" onClick={changePhone}>
                  <MdArrowBack size={19}/> Change Phone
                </button>
                <h6 className="customer-auth-title">Welcome back</h6>
                <p className="customer-auth-description">Sign in as {phone} using your password.</p>

                <div className="input-group mb-3">
                  <span className="input-group-text"><MdLock className="text-primary" size={21}/></span>
                  <input
                    className="form-control"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && submitPassword()}
                    placeholder="Password"
                  />
                </div>

                <button className="customer-auth-text-button mb-4" onClick={forgotPassword} disabled={loading}>
                  Forgot Password?
                </button>

                <button
                  className="btn btn-primary btn-rounded w-100 customer-auth-submit"
                  onClick={submitPassword}
                  disabled={loading}
                >
                  {loading ? getInlineLoader() : <>Sign In <MdLogin size={22}/></>}
                </button>
              </>
              : ""
          }

          {
            step === 'otp' ?
              <>
                <button className="customer-auth-back" onClick={changePhone}>
                  <MdArrowBack size={19}/> Change Phone
                </button>
                <h6 className="customer-auth-title">Verify your phone</h6>
                <p className="customer-auth-description">
                  Enter the six-digit OTP sent to {phone}, then create your account password.
                </p>

                <div className="input-group mb-3">
                  <span className="input-group-text"><MdPhone className="text-primary" size={21}/></span>
                  <input
                    className="form-control"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => setOtp(event.target.value)}
                    placeholder="Six-digit OTP"
                  />
                </div>

                <div className="input-group mb-3">
                  <span className="input-group-text"><MdLock className="text-primary" size={21}/></span>
                  <input
                    className="form-control"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="New password"
                  />
                </div>

                <div className="input-group mb-3">
                  <span className="input-group-text"><MdLock className="text-primary" size={21}/></span>
                  <input
                    className="form-control"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && submitOtp()}
                    placeholder="Confirm password"
                  />
                </div>

                <button className="customer-auth-text-button mb-4" onClick={resendOtp} disabled={loading}>
                  <MdRefresh size={18}/> Resend OTP
                </button>

                <button
                  className="btn btn-primary btn-rounded w-100 customer-auth-submit"
                  onClick={submitOtp}
                  disabled={loading}
                >
                  {loading ? getInlineLoader() : <>Verify and Continue <MdLogin size={22}/></>}
                </button>
              </>
              : ""
          }
        </div>
      </div>
    </div>
  )
}
